import React, { useEffect, useState } from 'react';

interface LoadingScreenProps {
    progress: number; // 0-100
    onComplete?: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ progress, onComplete }) => {
    const [fadeOut, setFadeOut] = useState(false);
    const [isStuck, setIsStuck] = useState(false);

    useEffect(() => {
        let stuckTimer: NodeJS.Timeout;
        if (progress === 0) {
            stuckTimer = setTimeout(() => {
                setIsStuck(true);
            }, 5000);
        } else {
            setIsStuck(false);
        }
        return () => clearTimeout(stuckTimer);
    }, [progress]);

    useEffect(() => {
        if (progress >= 100) {
            // Start fade out
            const timer = setTimeout(() => {
                setFadeOut(true);
                setTimeout(() => onComplete?.(), 500);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [progress, onComplete]);

    return (
        <div
            className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
        >
            {/* Logo/Title */}
            <div className="mb-12 text-center">
                <h1 className="text-6xl font-bold bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 bg-clip-text text-transparent drop-shadow-lg">
                    OKO
                </h1>
                <p className="mt-2 text-slate-400 text-lg tracking-wider">Card Game</p>
            </div>

            {/* Progress Bar Container */}
            <div className="w-80 h-3 bg-slate-700/50 rounded-full overflow-hidden border border-slate-600/30 shadow-inner">
                {/* Progress Fill */}
                <div
                    className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-full transition-all duration-300 ease-out shadow-lg relative"
                    style={{ width: `${Math.min(100, progress)}%` }}
                >
                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full" />
                    {/* Animated glow at end */}
                    <div className="absolute right-0 top-0 bottom-0 w-4 bg-white/40 blur-sm rounded-full animate-pulse" />
                </div>
            </div>

            {/* Progress Text */}
            <div className="mt-4 text-slate-400 text-sm flex items-center gap-2">
                <span className="tabular-nums">{Math.min(100, Math.round(progress))}%</span>
                <span className="text-slate-500">•</span>
                <span>{progress < 100 ? 'Loading assets...' : 'Ready!'}</span>
            </div>

            {/* Animated dots */}
            {progress < 100 && (
                <div className="mt-8 flex flex-col items-center gap-4">
                    <div className="flex gap-2">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-amber-500/60 rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.15}s` }}
                            />
                        ))}
                    </div>
                    {isStuck && (
                        <div className="mt-4 flex flex-col items-center gap-2 animate-fade-in">
                            <p className="text-amber-400/80 text-sm">Loading seems stuck.</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded border border-slate-600 transition-colors shadow-lg"
                            >
                                Refresh Page
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LoadingScreen;
